import { RANKS } from './data.js';

// Read-only merchant advice. Looking at the desk never changes prices or stock.
export function suggestedPrice(row) {
  return Math.min(1_000_000, Math.max(5, Math.ceil(Math.max(row.marketCents, row.avgCostCents + 5) / 5) * 5));
}

export function stockWarnings(row, minute) {
  if (!row.unlocked || row.qty < 1) return [];
  const warnings = [];
  if (row.marginCents <= 0) warnings.push({ id: 'margin', text: 'At or below cost' });
  if (!row.displayed) warnings.push({ id: 'shelf', text: 'In storage, off shelf' });
  if (row.listPriceCents > row.marketCents * 1.3) warnings.push({ id: 'price', text: 'Price well above market' });
  if (row.nextExpiryMinute != null && row.nextExpiryMinute - minute <= 1440) warnings.push({ id: 'expiry', text: 'Perishable: use within a day' });
  return warnings;
}

export function filterMarketRows(rows, filter, minute) {
  if (filter === 'shelf') return rows.filter(row => row.displayed || row.qty > 0 || row.buyCeilingCents > 0);
  if (filter === 'stock') return rows.filter(row => row.qty > 0);
  if (filter === 'attention') return rows.filter(row => stockWarnings(row, minute).length > 0);
  if (filter === 'all') return rows;
  return rows.filter(row => row.unlocked || row.qty > 0);
}

export function merchantSummary(state, derived) {
  const entries = state.ledger.filter(entry => entry.day === derived.day && Number.isFinite(entry.totalCents));
  const received = entries.reduce((sum, entry) => sum + Math.max(0, entry.totalCents), 0);
  const spent = entries.reduce((sum, entry) => sum - Math.min(0, entry.totalCents), 0);
  const partial = state.ledger.length > 0 && state.ledger[0].day === derived.day && state.ledger[0].id !== 'ledger-1';
  const nextRank = RANKS.find(rank => rank.level > state.rank);
  const progress = nextRank ? [
    { label: 'Reputation', value: state.reputation, target: nextRank.reputation },
    { label: 'Trades', value: state.transactions, target: nextRank.trades },
    { label: 'Day', value: derived.day, target: nextRank.day },
  ] : [];
  const issues = derived.ledgerRows.flatMap(row => stockWarnings(row, state.timeMinutes).map(warning => ({ ...warning, goodId: row.id, name: row.name })));
  const stocked = derived.ledgerRows.filter(row => row.qty > 0);
  const displayed = stocked.filter(row => row.displayed);
  const shelf = derived.ledgerRows.filter(row => row.unlocked && (row.displayed || row.qty > 0 || row.buyCeilingCents > 0));
  const fixedStale = shelf.filter(row => row.pricingMode !== 'market' && row.buyCeilingCents > 0 && (
    row.qty === 0 && row.buyCeilingCents < row.marketCents * .8 || row.listPriceCents > row.marketCents * 1.3
  ));
  let next;
  if (derived.opening.status !== 'completed') {
    next = { title: 'Make your first bargain', body: 'Eli Mercer is waiting with two pelts. Accept his price or counter; your first purchase opens automatic trade.', tab: 'meetings', action: 'MEET ELI MERCER' };
  } else if (issues.some(issue => issue.id === 'margin' || issue.id === 'expiry')) {
    next = { title: 'Give these goods some attention', body: 'Check perishable stock and prices at or below cost before the next buyer arrives.', tab: 'market', filter: 'attention', action: 'REVIEW STOCK' };
  } else if (!displayed.length && stocked.length) {
    next = { title: 'Put your stock to work', body: 'Goods sell automatically when displayed. Choose your shelf assortment in Market.', tab: 'market', filter: 'stock', action: 'ARRANGE THE SHELF' };
  } else if (!stocked.length) {
    next = { title: 'Bring the shelves back to life', body: 'Rope and dried provisions are available through local freight. Review cost, freight, and storage before ordering.', tab: 'orders', action: 'CHECK SUPPLIES' };
  } else if (derived.opening.laterTransactions < 4 && !derived.opening.landMarketOpen) {
    next = { title: 'Let the first customers come to you', body: 'Your displayed stock sells at your prices. Watch arrivals or use 2× / 4× while the post earns its place on the river.', tab: 'market', filter: 'stock', action: 'CHECK YOUR SHELF' };
  } else if (derived.plots.some(plot => plot.owned && (plot.damage.length || plot.condition < 60))) {
    next = { title: 'Keep the settlement working', body: 'A property needs repairs. Review its condition and the cost of materials and labor.', tab: 'build', action: 'INSPECT BUILDINGS' };
  } else {
    next = { title: 'Choose what this town becomes', body: derived.opening.landMarketOpen ? 'Land deeds are open. Keep a wild landing, develop river services, or build a workshop; every charter path stays viable.' : derived.opening.landMarketBlockReason, tab: 'build', action: 'EXPLORE THE SETTLEMENT' };
  }
  const demand = new Map();
  for (const visitor of derived.arrivals) {
    if (visitor.knowledge !== 'cargo' || !['scheduled', 'approaching', 'stopped', 'passing'].includes(visitor.status)) continue;
    for (const need of visitor.needs || []) {
      if (need.qty <= 0) continue;
      const row = derived.ledgerRows.find(item => item.id === need.goodId && item.unlocked);
      if (!row) continue;
      const existing = demand.get(row.id) || { id: row.id, name: row.name, wanted: 0, stocked: row.qty, displayed: row.displayed };
      existing.wanted += need.qty;
      demand.set(row.id, existing);
    }
  }
  return { received, spent, net: received - spent, partial, progress, nextRank, issues, stocked, displayed, shelf, fixedStale, next, demand: [...demand.values()].sort((a, b) => b.wanted - a.wanted).slice(0, 4) };
}
